import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import {
  getPurRecord,
  listPurRecords,
  PurClientConfig,
  PurPage,
  PurRecord,
  PurResource,
} from "react-lib/apis/PUR/purApi";
import { readId } from "react-lib/apis/PUR/purRead";
import { receiptOrderIssue } from "react-lib/apis/PUR/purReceiptCreate";

import PurLineTable from "./PurLineTable";
import PurApprovalActions from "./PurApprovalActions";
import PurRequestOrderPreparation from "./PurRequestOrderPreparation";
import { PurchaseDraft } from "./purDrafts";
import { formatMeerpMessage, formatPurTimestamp, purErrorMessageId, PUR_STATUS_LABELS } from "./purPresentation";

interface PurResourceListProps {
  client: PurClientConfig;
  resource: PurResource;
  initialStatus?: string;
  statuses: string[];
  allowAllStatuses?: boolean;
  onChoose?: (record: PurRecord) => void;
  onPrepareOrder?: (draft: PurchaseDraft) => void;
  onPrepareReceipt?: (record: PurRecord) => void;
  chooseLabel?: string;
  onOpenPurchaseOrder?: (id: number) => void;
  enableApproval?: boolean;
  onWorkflowLockChange?: (locked: boolean) => void;
  initialRecordId?: number | null;
}

const PAGE_SIZE = 20;

const ignoreLockChange = (_locked: boolean) => undefined;
const PurResourceList: React.FC<PurResourceListProps> = ({ client, resource, initialStatus = "", statuses, allowAllStatuses = true, onChoose, onPrepareOrder, onPrepareReceipt, chooseLabel = "MEERP_DRAFT_USE_PO", onOpenPurchaseOrder, enableApproval = false, onWorkflowLockChange = ignoreLockChange, initialRecordId = null }) => {
  const intl = useIntl();
  const t = (id: string) => formatMeerpMessage(intl, id);
  const [status, setStatus] = useState(initialStatus);
  const [offset, setOffset] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [listRevision, setListRevision] = useState(0);
  const [lookupIdText, setLookupIdText] = useState(initialRecordId === null ? "" : String(initialRecordId));
  const [workflowLocked, setWorkflowLocked] = useState(false);
  const [result, setResult] = useState<PurPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorId, setErrorId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(initialRecordId);
  const [detail, setDetail] = useState<PurRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErrorId, setDetailErrorId] = useState<string | null>(null);
  const handleWorkflowLock = useCallback((locked: boolean) => {
    setWorkflowLocked(locked); onWorkflowLockChange(locked);
  }, [onWorkflowLockChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setResult(null);
    setErrorId(null);
    listPurRecords(client, resource, { limit: PAGE_SIZE, offset, status })
      .then((page) => { if (active) setResult(page); })
      .catch((error: unknown) => { if (active) setErrorId(purErrorMessageId(error)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, resource, status, offset, refreshKey, listRevision]);

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      setDetailErrorId(null);
      return;
    }
    let active = true;
    setDetail(null);
    setDetailLoading(true);
    setDetailErrorId(null);
    getPurRecord(client, resource, selectedId)
      .then((record) => { if (active) setDetail(record); })
      .catch((error: unknown) => { if (active) setDetailErrorId(purErrorMessageId(error)); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [client, resource, selectedId, refreshKey]);

  const label = (value: string | number | null | undefined): string => value === null || value === undefined || value === "" ? t("MEERP_UNKNOWN") : String(value);
  const statusLabel = (value: string): string =>
    PUR_STATUS_LABELS.has(value) ? t(`MEERP_STATUS_${value}`) : label(value);
  const recordStatusLabel = (record: PurRecord): string =>
    resource === "purchaseOrders" && record.status === "COMPLETED" && record.percentBilled === null
      ? t("MEERP_COMPLETED_WITHOUT_BILLING_DATA") : statusLabel(record.status);
  const percent = (value: string | null): string => value == null ? t("MEERP_UNKNOWN") : intl.formatNumber(Number(value) / 100, { style: "percent", maximumFractionDigits: 2 });
  const isPurchaseOrder = resource === "purchaseOrders";
  const isReceipt = resource === "receipts";
  const pageNumber = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = result ? Math.max(1, Math.ceil(result.count / PAGE_SIZE)) : 1;

  return (
    <div className="card-meerp__workspace">
      <div className="card-meerp__toolbar">
        <label htmlFor="meerp-status-filter">{t("MEERP_STATUS_FILTER")}</label>
        <select id="meerp-status-filter" value={status} disabled={workflowLocked} onChange={(event) => {
          setStatus(event.target.value);
          setOffset(0);
          setSelectedId(null);
        }}>
          {allowAllStatuses && <option value="">{t("MEERP_STATUS_ALL")}</option>}
          {statuses.map((value) => <option value={value} key={value}>{statusLabel(value)}</option>)}
        </select>
        <button type="button" className="card-meerp__button card-meerp__secondary" disabled={workflowLocked} onClick={() => setRefreshKey((key) => key + 1)}>
          {t("MEERP_REFRESH")}
        </button>
        {result && <span className="card-meerp__count">{intl.formatMessage({ id: "MEERP_COUNT" }, { count: result.count })}</span>}
      </div>
      <p className="card-meerp__muted">{t("MEERP_TIMEZONE_NOTE")}</p>

      {isPurchaseOrder && <p className="card-meerp__muted">{t("MEERP_BILLING_NOT_PAYMENT")}</p>}
      {!allowAllStatuses && <p className="card-meerp__muted">{t("MEERP_SINGLE_STATUS_NOTE")}</p>}

      {loading && <p role="status" className="card-meerp__message">{t("MEERP_LOADING")}</p>}
      {!loading && errorId && (
        <div role="alert" className="card-meerp__message card-meerp__message--error">
          <span>{t(errorId)}</span>
          <button className="card-meerp__button" type="button" disabled={workflowLocked} onClick={() => setRefreshKey((key) => key + 1)}>{t("MEERP_RETRY")}</button>
        </div>
      )}
      {!loading && result?.records.length === 0 && <p className="card-meerp__message">{t("MEERP_EMPTY")}</p>}
      {!loading && result && result.records.length > 0 && (
        <div className="card-meerp__table-wrap">
          <table className="card-meerp__table">
            <caption className="card-meerp__sr-only">{t("MEERP_ITEMS")}</caption>
            <thead><tr>
              <th scope="col">{t("MEERP_NUMBER")}</th>
              <th scope="col">{t("MEERP_FIELD_DOCUMENT_DATE")}</th>
              <th scope="col">{t("MEERP_CREATED_AT")}</th>
              <th scope="col">{t(isPurchaseOrder ? "MEERP_VENDOR" : "MEERP_PROJECT")}</th>
              <th scope="col">{t("MEERP_STORAGE")}</th>
              <th scope="col">{t("MEERP_STATUS")}</th>
              {isPurchaseOrder && <><th scope="col">{t("MEERP_PERCENT_RECEIVED")}</th><th scope="col">{t("MEERP_PERCENT_BILLED")}</th></>}
              <th scope="col" className="card-meerp__numeric">{t(isPurchaseOrder ? "MEERP_TOTAL" : "MEERP_ITEMS")}</th>
              <th scope="col">{t("MEERP_DETAIL")}</th>
            </tr></thead>
            <tbody>{result.records.map((record) => (
              <tr key={record.id} className={selectedId === record.id ? "card-meerp__selected-row" : ""}>
                <td>{record.number}</td>
                <td>{label(record.date)}</td>
                <td>{formatPurTimestamp(intl, record.createdAt)}</td>
                <td>{label(isPurchaseOrder ? record.vendorName : record.projectNumber)}</td>
                <td>{label(record.storageName || record.storageId)}</td>
                <td><span className="card-meerp__status">{recordStatusLabel(record)}</span></td>
                {isPurchaseOrder && <><td>{percent(record.percentReceived)}</td><td>{percent(record.percentBilled)}</td></>}
                <td className="card-meerp__numeric">{isPurchaseOrder ? label(record.totalAmount) : record.itemCount ?? t("MEERP_UNKNOWN")}</td>
                <td><button type="button" className="card-meerp__link" disabled={workflowLocked} onClick={() => setSelectedId(record.id)}>{t("MEERP_OPEN_DETAIL")}</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {result && <div className="card-meerp__pagination">
        <button className="card-meerp__button" type="button" disabled={offset === 0 || loading || workflowLocked} onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setSelectedId(null); }}>{t("MEERP_PREVIOUS")}</button>
        <span>{intl.formatMessage({ id: "MEERP_PAGE" }, { current: pageNumber, total: pageCount })}</span>
        <button className="card-meerp__button" type="button" disabled={offset + PAGE_SIZE >= result.count || loading || workflowLocked} onClick={() => { setOffset(offset + PAGE_SIZE); setSelectedId(null); }}>{t("MEERP_NEXT")}</button>
      </div>}

      <details className="card-meerp__internal-lookup" data-testid="meerp-internal-lookup">
        <summary>{t("MEERP_INTERNAL_LOOKUP_SECTION")}</summary>
        <p className="card-meerp__muted">{t("MEERP_INTERNAL_LOOKUP_NOTE")}</p>
        <form className="card-meerp__search" onSubmit={(event) => {
          event.preventDefault();
          const id = readId(lookupIdText.trim());
          if (workflowLocked || id === null) return;
          if (selectedId === id) setRefreshKey((key) => key + 1);
          else setSelectedId(id);
        }}>
          <label htmlFor="meerp-record-id">{t("MEERP_RECORD_ID_LOOKUP")}</label>
          <input id="meerp-record-id" inputMode="numeric" value={lookupIdText} disabled={workflowLocked} onChange={(event) => setLookupIdText(event.target.value)} />
          <button className="card-meerp__button" type="submit" disabled={workflowLocked || readId(lookupIdText.trim()) === null}>{t("MEERP_RECORD_ID_OPEN")}</button>
        </form>
      </details>

      {selectedId !== null && <section className="card-meerp__detail" aria-label={t("MEERP_DETAIL")}>
        <div className="card-meerp__detail-header">
          <h3>{t("MEERP_DETAIL")}{detail ? ` · ${detail.number}` : ""}</h3>
          <button type="button" className="card-meerp__button card-meerp__secondary" disabled={workflowLocked} onClick={() => setSelectedId(null)}>{t("MEERP_CLOSE_DETAIL")}</button>
        </div>
        {detailLoading && <p role="status">{t("MEERP_LOADING")}</p>}
        {detailErrorId && !detailLoading && <p role="alert" className="card-meerp__error-text">{t(detailErrorId)}</p>}
        {detail && !detailLoading && <>
          <dl className="card-meerp__facts">
            <div><dt>{t("MEERP_RECORD_ID")}</dt><dd>{detail.id}</dd></div>
            <div><dt>{t("MEERP_STATUS")}</dt><dd>{recordStatusLabel(detail)}</dd></div>
            <div><dt>{t("MEERP_FIELD_DOCUMENT_DATE")}</dt><dd>{label(detail.date)}</dd></div>
            <div><dt>{t("MEERP_CREATED_AT")}</dt><dd>{formatPurTimestamp(intl, detail.createdAt)}</dd></div>
            <div><dt>{t("MEERP_EDITED_AT")}</dt><dd>{formatPurTimestamp(intl, detail.editedAt)}</dd></div>
            <div><dt>{t("MEERP_PROJECT")}</dt><dd>{label(detail.projectNumber)}</dd></div>
            <div><dt>{t("MEERP_VENDOR")}</dt><dd>{label(detail.vendorName)}</dd></div>
            <div><dt>{t("MEERP_STORAGE")}</dt><dd>{label(detail.storageName || detail.storageId)}</dd></div>
            {isReceipt && <>
              <div><dt>{t("MEERP_PO_ID")}</dt><dd>{label(detail.poId)}</dd></div>
              <div><dt>{t("MEERP_REJECTED_STORAGE")}</dt><dd>{label(detail.rejectedStorageName || detail.rejectedStorageId)}</dd></div>
            </>}
            {isPurchaseOrder && <>
              <div><dt>{t("MEERP_PERCENT_RECEIVED")}</dt><dd>{percent(detail.percentReceived)}</dd></div>
              <div><dt>{t("MEERP_PERCENT_BILLED")}</dt><dd>{percent(detail.percentBilled)}</dd></div>
            </>}
            {detail.totalAmount && <div><dt>{t("MEERP_TOTAL")}</dt><dd>{detail.totalAmount}</dd></div>}
            {detail.purpose && <div><dt>{t("MEERP_PURPOSE")}</dt><dd>{detail.purpose}</dd></div>}
            {detail.notes && <div><dt>{t("MEERP_NOTES")}</dt><dd>{detail.notes}</dd></div>}
          </dl>
          <h4>{t("MEERP_ITEMS")}</h4>
          {detail.itemCount === null ? <p>{t("MEERP_ITEMS_UNAVAILABLE")}</p> : detail.items.length === 0 ? <p>{t("MEERP_NO_ITEMS")}</p> : <PurLineTable resource={resource} lines={detail.items} />}
          {enableApproval && resource !== "receipts" && <PurApprovalActions key={`${resource}:${detail.id}`} client={client} kind={resource} record={detail}
            onLockChange={handleWorkflowLock}
            onUpdated={(updated) => { setDetail(updated); setListRevision((key) => key + 1); }} />}
          {resource === "materialRequests" && onPrepareOrder && detail.id === selectedId && <PurRequestOrderPreparation key={`request-order:${detail.id}`}
            client={client} record={detail} locked={workflowLocked} onLockChange={handleWorkflowLock} onPrepared={onPrepareOrder} />}
          {isPurchaseOrder && onPrepareReceipt && detail.id === selectedId && <button className="card-meerp__button" type="button" disabled={workflowLocked || Boolean(receiptOrderIssue(detail))} onClick={() => onPrepareReceipt(detail)}>{t("MEERP_DRAFT_USE_PO")}</button>}
          {isReceipt && onOpenPurchaseOrder && readId(detail.poId) !== null && <button className="card-meerp__button" type="button" disabled={workflowLocked} onClick={() => onOpenPurchaseOrder(readId(detail.poId)!)}>{t("MEERP_RECEIPT_OPEN_SOURCE_PO")}</button>}
          {onChoose && detail.id === selectedId && <button className="card-meerp__button" type="button" disabled={workflowLocked || !detail.items.length || (isPurchaseOrder && Boolean(receiptOrderIssue(detail)))} onClick={() => onChoose(detail)}>{t(chooseLabel)}</button>}
        </>}
      </section>}
    </div>
  );
};

export default PurResourceList;
